// owner-map: OP-07c.spa
// Sayer's own forecasts, offered on the new-question page (hooks/useFeaturedForecasts.js).
//
// Opening one gives the user a copy of it (one copy per question): the catalogue's version
// shows at once as a stand-in while the server creates the row from the stored report, which
// then replaces it. The copy follows Sayer's updates until the user does something only an
// owner can (hooks/useSayerCopyOwnership.js), which adopts it: the server drops its Sayer
// origin and leaves it on a resolution-only schedule (server/sayerForecasts/copy.js).
//
// The stand-in is `detailPending`, so the persister never saves it.
import {
  activeSet,
  detailHydrated,
  runCreated,
  runRemoved,
  sayerCopyAdopted,
} from "../lib/forecast/reducer.js";
import { rowToRun } from "../lib/forecast/hydrate.js";
import { selectLegacyForecastById } from "../lib/forecast/selectors.js";
import {
  findObserverCopy,
  observerClientId,
  observerStubRow,
} from "../lib/featured/observerCopy.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { beginIntent, type BeginIntentOptions } from "../lib/telemetry/instrument.js";
import { toast } from "../lib/ui/toast.js";
import type { CommandDeps } from "./deps.js";

/** One catalogue question, as GET /api/sayer-forecasts lists it. */
export interface SayerForecastItem {
  questionKey: string;
  [k: string]: unknown;
}

/**
 * Deps for opening a copy. `ui.open` opens a forecast the way a row click does and returns
 * false when it can't (the run is then selected in the store); `ui.beforeOpen` runs first, with
 * the copy's id.
 */
export interface SayerOpenDeps extends CommandDeps {
  ui: { open: (f: unknown) => boolean; beforeOpen?: (runId: string) => void };
}

function select(deps: SayerOpenDeps, runId: string): void {
  deps.ui.beforeOpen?.(runId);
  const f = selectLegacyForecastById(deps.store.getState(), runId);
  if (!f || !deps.ui.open(f)) deps.store.dispatch(activeSet(runId));
}

/**
 * Open the user's copy of a Sayer forecast, creating it when they have none. Resolves once the
 * copy has loaded, or has failed and its stand-in is gone.
 */
export async function openSayerForecast(
  rawDeps: SayerOpenDeps,
  { item, slot = null }: { item: SayerForecastItem; slot?: number | null },
): Promise<void> {
  const existing = findObserverCopy(
    Object.values(rawDeps.store.getState().runsById),
    item.questionKey,
  );
  // `matched`: the card is one the search put there for what the user is typing
  // (useFeaturedSearch); `personal`: one drawn from the user's own domains (pickFeatured);
  // `slot` its place on the page; `existing` the user had a copy already.
  const intent = beginIntent(
    "sayer_forecast.open",
    {
      question_key: item.questionKey,
      matched: item.related === true,
      personal: item.reason === "personal",
      slot,
      existing: !!existing,
    },
    { origin: "ask-hero.featured", surface: "ask-hero.featured" },
  );
  const deps = bindIntent(rawDeps, intent);
  const { store } = deps;
  if (existing) {
    select(deps, existing.runId);
    intent.end("ok");
    return;
  }
  const clientId = observerClientId(item.questionKey);
  const stub = rowToRun(observerStubRow(item, { clientId }));
  stub.detailPending = true;
  store.dispatch(runCreated(stub));
  select(deps, clientId);
  const r = await deps.services.api.copySayerForecast(item.questionKey, clientId);
  if (!r.ok || !r.row) {
    const wasActive = store.getState().activeRunId === clientId;
    store.dispatch(runRemoved(clientId));
    if (wasActive) store.dispatch(activeSet(null));
    toast("Couldn't open this forecast. Try again in a moment.", { kind: "error" });
    intent.end("error");
    return;
  }
  const run = rowToRun(r.row);
  if (run.runId === clientId) {
    store.dispatch(detailHydrated({ [clientId]: run }));
  } else {
    // A copy this tab had not loaded yet: show that one instead of a second.
    const wasActive = store.getState().activeRunId === clientId;
    store.dispatch(runRemoved(clientId));
    if (!store.getState().runsById[run.runId]) store.dispatch(runCreated(run));
    if (wasActive) select(deps, run.runId);
  }
  intent.end("ok");
}

/**
 * Make a copy of a Sayer forecast the user's own. Resolves true once it is (or already was).
 * `via` is what the user did that only an owner can; `origin` / `surface` its control and area.
 */
export async function adoptSayerForecast(
  rawDeps: CommandDeps,
  {
    runId,
    via,
    origin,
    surface,
  }: {
    runId: string;
    via: "schedule" | "update_now";
    origin?: BeginIntentOptions["origin"];
    surface?: BeginIntentOptions["surface"];
  },
): Promise<boolean> {
  const intent = beginIntent(
    "sayer_forecast.adopt",
    { forecast_id: runId, via },
    { ...(origin ? { origin } : {}), ...(surface ? { surface } : {}) },
  );
  const deps = bindIntent(rawDeps, intent);
  const r = await deps.services.api.adoptSayerForecast(runId);
  if (!r.ok || !r.row) {
    toast("Couldn't make this forecast yours. Try again in a moment.", { kind: "error" });
    intent.end("error");
    return false;
  }
  const run = rowToRun(r.row);
  // A copy loaded lean has no detail yet (its contract, among others), and the owner action that
  // follows needs it: fill it from the converted row. Nothing can have been picked up before
  // detail arrived, so there is no newer forecast for it to roll back.
  if (deps.store.getState().runsById[run.runId]?.detailPending) {
    deps.store.dispatch(detailHydrated({ [run.runId]: run }));
  }
  // Otherwise only what the conversion set: a full hydrate would roll back a newer forecast the
  // copy has picked up but not yet saved.
  if (!(run.serverContext as any)?.origin) {
    deps.store.dispatch(
      sayerCopyAdopted(run.runId, {
        schedule: run.schedule,
        contractKey: run.contractKey,
        contractStatus: run.contractStatus,
      }),
    );
  }
  if (r.converted) toast("This is now your forecast. Sayer's updates no longer apply to it.");
  intent.end("ok");
  return true;
}
